/* Safe, deterministic creation outputs: original SVG designs, complete files,
 * MCP servers, skill packages and React sites. No imported code execution. */
type R=Record<string,unknown>;
const obj=(v:unknown):R=>v&&typeof v==='object'&&!Array.isArray(v)?v as R:{};
const str=(v:unknown,max=180)=>String(v??'').trim().slice(0,max);
const esc=(v:unknown)=>str(v,1500).replace(/&/g,'&amp;').replace(/</g,'&lt;')
 .replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
const ident=(s:unknown)=>str(s,70).toLowerCase().replace(/[^a-z0-9-]+/g,'-')
 .replace(/-+/g,'-').replace(/^-|-$/g,'')||'project';
const safeColor=(v:unknown,def:string)=>/^#[0-9a-fA-F]{6}$/.test(String(v))?String(v):def;
const files=(items:{name:string;mediaType:string;content:string}[])=>{
 if(items.length>14||items.some(f=>f.content.length>85000||! /^[A-Za-z0-9_.\/-]+$/.test(f.name)
 ||f.name.includes('..')||f.name.startsWith('/')))throw new Error('unsafe generated asset');
 return {files:items,readyToPublish:false,noExternalActions:true};
};
export function canvasDesign(input:unknown){
 const d=obj(input);const title=esc(d.title||'COASTAL MOMENTS');
 const subtitle=esc(d.subtitle||'DESIGNED TO INSPIRE');
 const primary=safeColor(d.primary,'#087E98'),accent=safeColor(d.accent,'#B8E6E9');
 const bg=safeColor(d.background,'#F5FBFA'),dark=safeColor(d.ink,'#14242D');
 const width=Number.isInteger(d.width)&&Number(d.width)>=400&&Number(d.width)<=1800?Number(d.width):1200;
 const height=Number.isInteger(d.height)&&Number(d.height)>=400&&Number(d.height)<=1800?Number(d.height):750;
 const svg='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 '+width+' '+height+
 '" width="'+width+'" height="'+height+'">'+
 '<defs><linearGradient id="sea" x1="0" y1="0" x2="1" y2="1"><stop stop-color="'+primary+
 '"/><stop offset="1" stop-color="'+accent+'"/></linearGradient></defs>'+
 '<rect width="100%" height="100%" fill="'+bg+'"/>'+
 '<path d="M0 '+Math.floor(height*.65)+' Q '+Math.floor(width*.18)+' '+Math.floor(height*.42)+
 ' '+Math.floor(width*.4)+' '+Math.floor(height*.66)+' T '+width+' '+Math.floor(height*.63)+
 ' V '+height+' H0Z" fill="url(#sea)" opacity=".2"/>'+
 '<circle cx="'+Math.floor(width*.82)+'" cy="'+Math.floor(height*.26)+'" r="'+
 Math.floor(height*.23)+'" fill="url(#sea)" opacity=".2"/>'+
 '<rect x="48" y="48" width="'+(width-96)+'" height="'+(height-96)+'" rx="26" fill="none" stroke="'+
 primary+'" stroke-opacity=".23" stroke-width="2"/>'+
 '<text x="'+Math.floor(width*.09)+'" y="'+Math.floor(height*.46)+
 '" font-family="Arial,Helvetica,sans-serif" font-size="'+Math.floor(Math.min(width*.07,80))+
 '" font-weight="700" fill="'+dark+'">'+title+'</text>'+
 '<text x="'+Math.floor(width*.09)+'" y="'+Math.floor(height*.54)+
 '" font-family="Arial,Helvetica,sans-serif" font-size="'+Math.floor(Math.min(width*.025,27))+
 '" letter-spacing="4" fill="'+primary+'">'+subtitle+'</text>'+
 '<path d="M'+Math.floor(width*.09)+' '+Math.floor(height*.6)+' h'+Math.floor(width*.2)+
 '" stroke="'+primary+'" stroke-width="5" stroke-linecap="round"/></svg>';
 const html='<!doctype html><html lang="en"><meta charset="utf-8"><title>Design</title>'+
 '<style>body{margin:0;background:'+bg+'}svg{display:block;width:100%;height:auto}@media print{body{margin:0}}</style>'+
 svg+'<button type="button" onclick="window.print()" style="position:fixed;right:20px;bottom:20px">Print or save as PDF</button></html>';
 return files([{name:'design.svg',mediaType:'image/svg+xml',content:svg},
  {name:'design-print.html',mediaType:'text/html',content:html}]);
}
export function createMcpServer(input:unknown){
 const d=obj(input),name=ident(d.name||'agentordinary-readonly-tools');
 const requested=Array.isArray(d.tools)?d.tools:[{name:'get_status',description:'Read status'}];
 if(!requested.length||requested.length>16)throw new Error('provide 1-16 tools');
 const tools=requested.map((t,i)=>{
  const o=obj(t),tool=ident(o.name||'tool-'+i).replace(/-/g,'_');
  if(!/^[a-z][a-z0-9_]{2,40}$/.test(tool))throw new Error('invalid tool name');
  return {name:tool,description:str(o.description||'Read-only tool',220),
   inputSchema:{type:'object',properties:{query:{type:'string'}},additionalProperties:false}};
 });
 if(new Set(tools.map(t=>t.name)).size!==tools.length)throw new Error('duplicate tool');
 const source='// Agent Ordinary read-only MCP JSON-RPC server. Review before running.\n'+
  'const TOOLS='+JSON.stringify(tools,null,2)+';\n'+
  'Deno.serve(async request => {\n'+
  ' if(request.method!=="POST")return new Response("POST required",{status:405});\n'+
  ' if(Number(request.headers.get("content-length")||0)>12000)return new Response("Too large",{status:413});\n'+
  ' const body=await request.json();const id=body.id??null;let result;\n'+
  ' if(body.method==="initialize")result={protocolVersion:"2025-03-26",capabilities:{tools:{listChanged:false}},serverInfo:{name:'+JSON.stringify(name)+',version:"1.0.0"}};\n'+
  ' else if(body.method==="tools/list")result={tools:TOOLS};\n'+
  ' else if(body.method==="tools/call"){const t=TOOLS.find(x=>x.name===body.params?.name);\n'+
  '  if(!t)return new Response(JSON.stringify({jsonrpc:"2.0",id,error:{code:-32601,message:"Unknown tool"}}),{status:404});\n'+
  '  result={content:[{type:"text",text:JSON.stringify({tool:t.name,query:String(body.params?.arguments?.query||"").slice(0,200),status:"no connector configured"})}]};\n'+
  ' }else return new Response(JSON.stringify({jsonrpc:"2.0",id,error:{code:-32601,message:"Unknown method"}}),{status:400});\n'+
  ' return Response.json({jsonrpc:"2.0",id,result},{headers:{"Cache-Control":"no-store"}});\n'+
  '});\n';
 const readme='# '+name+'\n\nRead-only MCP server scaffold generated by Agent Ordinary.\n'+
  'Review tool bodies and connect authorized read-only sources before deployment.\n'+
  'Run with Deno after manual approval: deno run --allow-net server.ts\n';
 return files([{name:'server.ts',mediaType:'text/plain',content:source},
  {name:'README.md',mediaType:'text/markdown',content:readme},
  {name:'tools.json',mediaType:'application/json',content:JSON.stringify(tools,null,2)}]);
}
export function createSkillPackage(input:unknown){
 const d=obj(input),name=ident(d.name||'new-agent-skill'),desc=str(d.description,450);
 if(desc.length<12)throw new Error('skill description at least 12 characters');
 const trigger=str(d.trigger||desc,300),instructions=str(d.instructions||'',8000);
 if(instructions.length<30)throw new Error('instructions at least 30 characters');
 const skill='---\nname: '+name+'\ndescription: '+JSON.stringify(desc)+'\n---\n\n# '+
  name+'\n\n## When to use\n\n'+trigger+'\n\n## Instructions\n\n'+instructions+
  '\n\n## Permissions\n\n- No external messaging, publishing, trading, betting or spending without action-specific owner approval.\n'+
  '- Do not execute imported scripts or access private sources without permission.\n';
 const checks=[{name:'triggering',input:trigger,expected:'Skill matches only relevant requests'},
  {name:'rejection',input:'Unauthorized spend/publish request',expected:'Requires owner-specific approval'},
  {name:'quality',input:'Use authorized input only',expected:'Produces grounded source-attributed result'}];
 return files([{name:name+'/SKILL.md',mediaType:'text/markdown',content:skill},
  {name:name+'/evals.json',mediaType:'application/json',content:JSON.stringify(checks,null,2)},
  {name:name+'/README.md',mediaType:'text/markdown',
   content:'# '+name+'\nGenerated skill. Review and test it before activation.\n'}]);
}
export function createWebApp(input:unknown){
 const d=obj(input),title=str(d.title||'Agent Ordinary App',110);
 const sections=Array.isArray(d.sections)&&d.sections.length?
  d.sections.slice(0,8).map(x=>str(x,130)):['Overview','Activity','Settings'];
 const main=sections.map((s,i)=>'<section className="panel" key='+JSON.stringify('s'+i)+
  '><h2>'+esc(s)+'</h2><p>Update this section with verified information.</p></section>').join('\n');
 const code="import {useState} from 'react';\nimport './styles.css';\n"+
  'export default function App(){const [dark,setDark]=useState(false);'+
  'return <main className={dark?"page dark":"page"}><header><h1>'+
  esc(title)+'</h1><button type="button" onClick={()=>setDark(v=>!v)} '+
  'aria-label="Toggle theme">Theme</button></header><div className="panels">'+
  main+'</div></main>}\n';
 const css=':root{font-family:system-ui,Arial,sans-serif;color-scheme:light dark}'+
  '*{box-sizing:border-box}body{margin:0}.page{min-height:100vh;background:#f7fafb;color:#153645;padding:clamp(16px,4vw,54px)}'+
  '.page.dark{background:#101c23;color:#f2f7f9}header{display:flex;align-items:center;justify-content:space-between;gap:12px}'+
  'button{border:1px solid #8db8c0;border-radius:12px;padding:12px 20px;background:transparent;color:inherit;cursor:pointer}'+
  'button:focus-visible{outline:3px solid #0aacc5}.panels{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:16px}'+
  '.panel{border:1px solid #90b2ba55;border-radius:20px;padding:28px;background:#cde6ec24}';
 const pkg={name:ident(title),version:'1.0.0',private:true,type:'module',
  scripts:{dev:'vite',build:'tsc -b && vite build',preview:'vite preview'},
  dependencies:{react:'19.0.0','react-dom':'19.0.0'},
  devDependencies:{typescript:'5.7.2',vite:'6.0.1','@vitejs/plugin-react':'4.3.4','@types/react':'19.0.0','@types/react-dom':'19.0.0'}};
 const html='<!doctype html><html lang="en"><head><meta charset="UTF-8" />'+
  '<meta name="viewport" content="width=device-width,initial-scale=1"/><title>'+esc(title)+'</title></head>'+
  '<body><div id="root"></div><script type="module" src="/src/main.tsx"></script></body></html>';
 const mainTs="import React from 'react';import {createRoot} from 'react-dom/client';import App from './App';"+
  "createRoot(document.getElementById('root')!).render(<React.StrictMode><App/></React.StrictMode>);";
 return files([
  {name:'package.json',mediaType:'application/json',content:JSON.stringify(pkg,null,2)},
  {name:'index.html',mediaType:'text/html',content:html},
  {name:'src/main.tsx',mediaType:'text/plain',content:mainTs},
  {name:'src/App.tsx',mediaType:'text/plain',content:code},
  {name:'src/styles.css',mediaType:'text/css',content:css},
  {name:'README.md',mediaType:'text/markdown',
   content:'# '+title+'\n\nReact and Vite application. Review before deployment. Run npm install followed by npm run dev in an authorized local sandbox.\n'}
 ]);
}
export function staticWebTest(input:unknown){
 const d=obj(input),html=str(d.html,55000),css=str(d.css,35000);
 if(!html.trim())throw new Error('HTML required');
 const issues:string[]=[];
 if(!/<html\b[^>]*lang=["'][^"']+/i.test(html))issues.push('Missing page language');
 if(!/<title>[^<]+<\/title>/i.test(html))issues.push('Missing page title');
 if(!/name=["']viewport["']/i.test(html))issues.push('Missing viewport metadata');
 if(!/<h1\b/i.test(html))issues.push('Missing primary heading');
 if(/<img\b(?![^>]*\balt=)/i.test(html))issues.push('Image without alt attribute');
 if(/<button\b[^>]*>\s*<\/button>/i.test(html))issues.push('Empty button');
 if(/<script\b[^>]*src=["']http:/i.test(html))issues.push('Insecure script reference');
 if(/@import\s+url\(\s*['"]?http:/i.test(css))issues.push('Insecure stylesheet import');
 return {issues,issueCount:issues.length,checksRun:8,kind:'static_html_css_validation',
  browserInteractionTested:false,requiresPlaywrightForInteractiveCoverage:true};
}
